import React, { useCallback, useEffect, useMemo } from "react";
import {
  Box,
  CircularProgress,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  OutlinedInput,
  Stack,
  styled,
  SxProps,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { SIDEBAR_WIDTH, SIDEBAR_WIDTH_CLOSED } from "../../../Sidebar/sidebar";
import MovieCard from "../../../Cards/MovieCard/movie-card";
import {
  AutocompleteCategory,
  AutocompleteFilter,
  FilterTags,
} from "../../../Search/AutocompleteSearch/autocomplete-search";
import { useLocation, useNavigate } from "react-router";
import { routePaths } from "../../../../Router/router";
import CollectionCard from "../../../Cards/CollectionCard";
import {
  assetService,
  searchService,
  thumbnailService,
  userService,
  cacheService,
} from "../../../../Core/Api/services";
import { DbSearchHistoryInfos } from "../../../../Core/Api/Types/db-search-history-infos";
import { ObjectLayer } from "../../../../Core/Api/Types/object-layer";
import { ThumbnailSize } from "../../../../Core/Api/Thumbnail/thumbnail-api-service";
import { throttle } from "throttle-debounce";
import { DbAssetObjectInfos } from "../../../../Core/Api/Types/db-asset-object-infos";
import { GetMetadataByTagName } from "../../../../Core/Utils/asset-object-utils";
import GlobalConst from "../../../../Core/Utils/Constants/globals";
import { DbObjectCollectionInfos } from "../../../../Core/Api/Types/db-object-collection-infos";
import { WishlistCollectionCardProps } from "../../../Cards/WishlistCollectionCard/wishlist-collection-card";
import { PlatformMode } from "../../../../Core/Api/Types/platform-mode";
import { IsStringAnId } from "../../../../Utils/helpers";
import { SearchRequestNative } from "../../../../Core/Api/Types/search-request-native";
import { SetSearchPattern } from "../../../../Redux/Reducers/redux-slice-filters";
import { useAppDispatch } from "../../../../Redux/redux-store";
import { useSelector } from "react-redux";
import { GetSidebarOpen } from "../../../../Redux/Reducers/redux-slice-settings-users";
import { ObjectClass } from "../../../../Core/Api/Types/object-class";
import { useTranslation } from "react-i18next";

export interface PageHeadSearchProps {
  sx?: SxProps;
  searchOpen: boolean;
  setSearchOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const PageHeadSearch: React.FC<PageHeadSearchProps> = (props) => {
  const { searchOpen, setSearchOpen } = props;
  const { t } = useTranslation();
  const [query, setQuery] = React.useState("");
  const [searchHistory, setSearchHistory] = React.useState<DbSearchHistoryInfos[]>([]);
  const [typeheadSearchResults, setTypeheadSearchResults] = React.useState<DbAssetObjectInfos[]>([]);
  const [typeheadSearchFirstLoaded, setTypeheadSearchFirstLoaded] = React.useState<boolean>(false);
  const [tagsGenres, setTagsGenres] = React.useState<FilterTags[]>([]);
  const [tagActive, setTagActive] = React.useState<string>("");
  const [loading, setLoading] = React.useState(false);
  const [collectionData, SetCollectionData] = React.useState<DbObjectCollectionInfos[]>([]);
  const [collectionDataDefault, SetCollectionDataDefault] = React.useState<DbObjectCollectionInfos[]>([]);
  const { id: userId, groupId } = cacheService.GetUserInfo()!;
  const openSidebar = useSelector(GetSidebarOpen);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const metadataList = cacheService.GetMetadata();
  const platformMode: PlatformMode = cacheService.GetPlatformMode();

  const handleSetTagFilter = (tag: string) => {
    if (tagActive === tag) setTagActive("");
    else setTagActive(tag);
  };

  const isAssetIdSearch = useMemo((): boolean => IsStringAnId(query), [query]);
  const isDrive = useMemo(
    () => platformMode !== PlatformMode.Distribution || location.pathname.startsWith(routePaths.drive.path),
    [platformMode, location.pathname]
  );

  const throttledTypeheadSearch = useMemo(
    () =>
      throttle(
        GlobalConst.SEARCH_THROTTLE_DELAY_MS, // Wait half sec
        (searchValue: string) => {
          let request: SearchRequestNative = new SearchRequestNative({
            hideTitleWithoutAsset: false,
            onlyConfidential: false,
            pattern: searchValue,
            outputLayer: isDrive ? ObjectLayer.Asset : ObjectLayer.Title,
            classifications: isDrive ? [ObjectClass.Drive] : null,
            limit: 10, // 10 latest
          });

          setLoading(true);

          searchService.SearchBasic(request).then((response) => {
            assetService.SearchCollectionByObjectIds(response.results?.map<number>((x) => x.id!) ?? []).then((data) => {
              if (data.length === 0) SetCollectionData(collectionDataDefault);
              else SetCollectionData(data);
            });

            if (response.results) setTypeheadSearchResults(response.results);

            // Compute tags
            let tags: FilterTags[] = [];

            response.results?.forEach((asset) => {
              let genres = GetMetadataByTagName(asset, "titleGenres", metadataList)?.value;

              if (genres) {
                let array = genres as string[];

                array.forEach((element) => {
                  let index = tags.findIndex((x) => x.tag === element);

                  if (index !== -1) tags[index].count++;
                  else tags.push({ tag: element, count: 1 });
                });
              }
            });

            setTagsGenres(tags);
            setLoading(false);

            if (!typeheadSearchFirstLoaded) setTypeheadSearchFirstLoaded(true);
          });
        },
        {
          noLeading: true,
        }
      ),
    [isDrive]
  );

  const handleSearch: React.ChangeEventHandler<HTMLInputElement> = React.useCallback((e) => {
    setQuery(e.target.value);

    if (e.target.value !== "" && e.target.value !== "*" && e.target.value.length >= 3)
      throttledTypeheadSearch(e.target.value);
  }, []);

  const collectionListData: {
    list: Omit<WishlistCollectionCardProps, "selectMode" | "index">[];
  } = React.useMemo(
    () => ({
      list: collectionData.map((collection) => {
        const covers =
          collection?.objectIds != undefined && collection?.objectIds?.length > 4
            ? collection.objectIds
                .filter(() => {
                  return !(
                    !collection.targetGroupIds?.includes(groupId!) && collection.targetUserIds?.includes(userId!)
                  );
                })
                ?.slice(0, -4)
                .map<string>((objectId) => thumbnailService.GetObjectPosterUrl(objectId, ThumbnailSize.small))
            : collection.objectIds?.map<string>((objectId) =>
                thumbnailService.GetObjectPosterUrl(objectId, ThumbnailSize.medium)
              ) || [];

        return {
          isCreationCard: false,
          id: collection.id,
          title: collection.name!,
          nbTitles: collection.objectInfos?.filter((x) => x.layer === ObjectLayer.Title).length || 0,
          nbTotaleItem: collection.objectIds?.length || 0,
          covers: [covers[0] || "", covers[1] || "", covers[2] || "", covers[3] || ""],
          selectMode: false,
          creationInfos: collection.creationInfos,
        };
      }),
    }),
    [collectionData]
  );

  const handleKeyDown = useCallback(
    (event: any) => {
      if (event.key === "Enter") {
        if (!isAssetIdSearch) {
          if (isDrive) {
            navigate(`${routePaths.drive.path}`);
            dispatch(SetSearchPattern(query));
          } else {
            navigate(`${routePaths.catalog.path}/find/${query}`);
          }
        } else navigate(`${routePaths.asset.path}/${query}`);

        setSearchOpen(false);
      }
      if (event.key === "Escape") {
        setQuery("");
        inputRef.current?.blur();
        setSearchOpen(false);
      }
    },
    [isAssetIdSearch, query, isDrive]
  );

  useEffect(() => {
    if (!searchOpen) return;

    userService.GetSearchHistory().then(function (response) {
      if (response.searchHistory !== null) {
        let layer = isDrive ? ObjectLayer.Asset : ObjectLayer.Title;
        setSearchHistory(response.searchHistory.filter((x) => x.layer === layer));
      }
    });

    assetService.ListCollection().then((data) => {
      SetCollectionDataDefault(data.slice(-6));
      SetCollectionData(data.slice(-6));
    });
  }, [searchOpen]);

  //We close search on navigate
  useEffect(() => {
    setSearchOpen(false);
  }, [location.pathname]);

  const inputRef = React.useRef<HTMLInputElement>(undefined);

  const topBar = useMemo(() => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={2}>
          <Typography variant="subtitle2" fontWeight={600} mb={2}>
            {t("common:suggestions")}
          </Typography>
          <Stack spacing={1.5}>
            <Link
              key="latest-titles"
              underline="hover"
              variant="body1"
              color="text.tier"
              onClick={() => {
                navigate(routePaths.catalog.path);
              }}
            >
              {isDrive ? t("button:latestAssets") : t("button:latestTitles")}
            </Link>
            {collectionDataDefault &&
              collectionDataDefault.length >= 0 &&
              collectionDataDefault.map((x) => (
                <Link
                  key={x.id}
                  underline="hover"
                  variant="body1"
                  color="text.tier"
                  onClick={() => {
                    navigate(routePaths.collections.path + "/" + x.id);
                  }}
                >
                  {x.name}
                </Link>
              ))}
          </Stack>
        </Grid>
        <Grid item xs={10}>
          <Typography variant="subtitle2" fontWeight={600} mb={2}>
            {t("component:body.recentViews")}
          </Typography>
          <Grid container spacing={2} columns={{ sm: 10, lg: 15, xl: 20 }}>
            {searchHistory.map((movie, index) => (
              <Grid item xs={2} key={movie.objectId} sx={{ alignItems: "stretch" }}>
                <MovieCard
                  title={movie.description!}
                  id={movie.objectId!}
                  cover={thumbnailService.GetObjectPosterUrl(movie.objectId!, ThumbnailSize.small)}
                  transparentChipText={"#" + movie.objectId!}
                  labelList={[]}
                  favorite={false}
                  expand={{ tagList: [], contentList: [], audioList: [], subtitleList: [], quality: "" }}
                  noHover={true}
                  isDrive={isDrive}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    );
  }, [thumbnailService, searchHistory, collectionDataDefault, routePaths]);

  return (
    <>
      <StyledOutlinedInput
        label={t("common:standard")}
        inputRef={inputRef}
        placeholder={t("common:search")}
        value={query}
        size="small"
        sx={(theme) => ({
          width: searchOpen ? 300 : 150,
          position: searchOpen ? "absolute" : "relative",
          left: searchOpen ? theme.spacing(3) : 0,
        })}
        endAdornment={
          <InputAdornment position="end">
            {query && (
              <IconButton aria-label="Clear field" onClick={() => setQuery("")} size="small" sx={{ mr: 1 }}>
                <CloseRoundedIcon />
              </IconButton>
            )}
            <SearchIcon />
          </InputAdornment>
        }
        onClick={(e) => {
          if (!searchOpen) {
            setSearchOpen(true);
          }
          e.stopPropagation();
        }}
        onChange={handleSearch}
        onKeyDown={handleKeyDown}
      />
      <DrawerSearch
        anchor="top"
        open={searchOpen}
        sidebarOpened={openSidebar}
        onClose={() => setSearchOpen(false)}
        disableAutoFocus={true}
        disableEnforceFocus={true}
        disableRestoreFocus={true}
        query={query}
        onClick={(e) => e.stopPropagation()}
        className="search-drawer"
      >
        <Stack
          sx={(theme) => ({
            padding: theme.spacing(1),
            height: "100%",
          })}
        >
          <Stack
            sx={(theme) => ({
              backgroundColor: theme.palette.background[200],
              padding: theme.spacing(2),
              paddingTop: `calc(${GlobalConst.HEADER_HEIGHT}px + 16px)`,
              borderRadius: 1,
              height: "calc(100% + 16px)",
              overflowY: "scroll",
              ...theme.helpers(theme).panelHighlights,
            })}
          >
            {query === "" ? (
              topBar
            ) : (
              <Stack direction="column" sx={{ height: "100%" }}>
                <AutocompleteFilter
                  tags={tagsGenres}
                  activeTags={[tagActive]}
                  onClick={(activeTag) => handleSetTagFilter(activeTag)}
                  sx={{ mb: 3 }}
                />

                <Grid container spacing={2} sx={{ flexShrink: 1, minHeight: 0 }}>
                  <Grid item xs={5} sx={{ height: "100%", overflowY: "scroll" }}>
                    {typeheadSearchFirstLoaded && (
                      <AutocompleteCategory
                        title={isDrive ? t("common:assets") : t("common:titles")}
                        assets={typeheadSearchResults.filter(
                          (x) =>
                            tagActive === "" ||
                            GetMetadataByTagName(x, "titleGenres", metadataList)?.value?.includes(tagActive)
                        )}
                        metadataList={metadataList}
                        key="category-titles-topbar"
                        onSearchResultClick={() => {
                          setSearchOpen(false);
                          setQuery("");
                        }}
                        loading={loading}
                        isDrive={isDrive && platformMode !== PlatformMode.Adam}
                      />
                    )}
                    {!typeheadSearchFirstLoaded && <CircularProgress sx={{}} />}
                  </Grid>
                  <Grid item xs={6} sx={{ marginLeft: "8.3%" }}>
                    <Box
                      sx={{
                        display: "flex",
                        gridGap: "24px",
                        flexWrap: "wrap",
                      }}
                    >
                      {collectionListData.list.map((collection) => (
                        <CollectionCard
                          to={routePaths.collections.path + "/" + collection.id}
                          nbitem={collection.nbTotaleItem}
                          key={collection.id}
                          covers={collection.covers}
                          title={collection.title}
                          sx={{ height: 240, width: 240, p: 3 }}
                        />
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </Stack>
            )}
          </Stack>
        </Stack>
      </DrawerSearch>
    </>
  );
};

const DrawerSearch = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "query" && prop !== "sidebarOpened",
})<{ query: string; sidebarOpened: boolean }>(({ theme, query, sidebarOpened }) => ({
  ".MuiDrawer-paper": {
    left: sidebarOpened ? `calc(${SIDEBAR_WIDTH}px + 8px)` : `calc(${SIDEBAR_WIDTH_CLOSED}px + 8px)`,
    // transition: theme.transitions.create(['transform', 'height'], {
    //   easing: theme.transitions.easing.sharp,
    //   duration: theme.transitions.duration.enteringScreen,
    // })+" !important",
    height: 600,
    background: "transparent",
    ...(query && {
      height: "100%",
    }),
  },
  "&.is-hidden": {
    opacity: 0,
    pointerEvents: "none",
  },
}));
export default PageHeadSearch;

const StyledOutlinedInput = styled(OutlinedInput)<{}>(({ theme }) => ({
  transition: "opacity .6s cubic-bezier(0.22, 1, 0.36, 1)",
}));
